<template lang="html">
  <v-container fill-width class="my-0 py-0" :style="style">
    <v-dialog v-model="textboxDialogOpened" persistent width="500">
      <v-card>
        <v-card-text>
          <v-textarea
            class="default-input"
            solo
            flat
            hide-details
            v-model="textboxText"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="text-capitalize primary" @click="saveTextbox()">
            Save
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="textboxDialogOpened = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="passwordDialogOpened" persistent width="500">
      <v-card>
        <v-card-text>
          <p class="text-xs-left">
            The PDF file you are trying to open requires a password for access.
            Please enter password below to proceed.
          </p>

          <v-text-field
            class="default-input"
            solo
            flat
            type="password"
            placeholder="Enter password"
            v-model="pdfPassword"
          />

          <p
            class="text-xs-left red--text"
            v-if="passwordReason === 'INCORRECT_PASSWORD'"
          >
            Incorrect password. Please try again.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="text-capitalize primary" @click="submitPassword()">
            Save
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="passwordDialogOpened = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-toolbar class="mb-2 sticky-toolbar" v-if="pdfLoaded" flat>
      <v-spacer v-if="!showAnnotations" />
      <v-icon
        class="grey--text text--darken-1 mr-1"
        style="cursor: pointer"
        @click="currentPage -= 1"
        :disabled="currentPage === 1"
        large
        >keyboard_arrow_left</v-icon
      >
      <v-flex class="grey--text text--darken-3 subheading font-weight-bold"
        >{{ currentPage }} / {{ pageCount }}</v-flex
      >
      <v-icon
        class="grey--text text--darken-1 mx-1"
        style="cursor: pointer"
        @click="currentPage += 1"
        :disabled="currentPage === pageCount"
        large
        >keyboard_arrow_right</v-icon
      >
      <v-icon
        v-if="!hideRotateButton"
        class="grey--text text--darken-1 mx-1"
        style="cursor: pointer"
        @click="previewRotation += 90"
        >rotate_right</v-icon
      >
      <v-tooltip bottom>
        <v-btn
          icon
          class="default-input text-none my-0 py-0 darkgrey--text"
          @click="zoom = !zoom"
          slot="activator"
        >
          <v-icon size="30">{{ zoom ? 'zoom_out' : 'zoom_in' }}</v-icon>
        </v-btn>
        <span>Zoom {{ zoom ? 'Out' : 'In' }}</span>
      </v-tooltip>
      <v-spacer />
      <v-layout v-if="showAnnotations" justify-end>
        <v-btn-toggle v-model="shapeType" class="transparent" mandatory>
          <v-tooltip bottom>
            <v-btn slot="activator" value="move" flat>
              <v-icon>open_with</v-icon>
            </v-btn>
            <span>Move</span>
          </v-tooltip>

          <v-tooltip bottom>
            <v-btn slot="activator" value="check" flat>
              <v-icon>done</v-icon>
            </v-btn>
            <span>Checkmark</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" value="textbox" flat>
              <v-icon>title</v-icon>
            </v-btn>
            <span>Textbox</span>
          </v-tooltip>

          <v-tooltip bottom>
            <v-btn slot="activator" value="delete" flat>
              <v-icon>delete</v-icon>
            </v-btn>
            <span>Delete</span>
          </v-tooltip>
        </v-btn-toggle>
        <v-divider vertical class="mx-2" />
        <v-btn-toggle v-model="selectedColor" class="transparent" mandatory>
          <v-btn value="black" flat>
            <v-icon class="filled black--text">square</v-icon>
          </v-btn>
          <v-btn value="blue" flat>
            <v-icon class="filled primary--text">square</v-icon>
          </v-btn>
          <v-btn value="green" flat>
            <v-icon class="filled green--text">square</v-icon>
          </v-btn>
          <v-btn value="red" flat>
            <v-icon class="filled red--text">square</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-layout>
    </v-toolbar>

    <v-layout column align-center v-if="previewType === 'pdf'">
      <v-container
        class="ma-0 pa-0 pdf-container"
        v-on:click="getLocation"
        :style="zoom ? 'max-height: 500px' : 'max-width: 600px;'"
      >
        <v-layout v-if="errorMessage">
          <v-icon class="material-icons-outlined mr-2">info</v-icon>
          <span style="font-size: 16px">
            {{ errorMessage }}
          </span>
        </v-layout>

        <v-progress-circular
          v-else-if="!pdfLoaded"
          indeterminate
          size="50"
          class="primary--text ma-5"
        />
        <pdf
          :src="documentPreviewData"
          :rotate="previewRotation"
          @num-pages="pageCount = $event"
          @page-loaded="
            (currentPage = $event), (pdfLoaded = true);
            renderAnnotations();
          "
          @password="handlePassword"
          :page="currentPage"
        >
        </pdf>
        <v-layout
          @click="takeAction(index)"
          :style="shapeType === 'delete' ? 'cursor: pointer' : ''"
          v-for="(annotation, index) in annotationsInSamePage"
          :key="index"
          v-if="pdfLoaded && isAdvisor && showAnnotations"
        >
          <v-tooltip
            bottom
            :disabled="moving"
            close-delay="100"
            open-delay="100"
          >
            <v-layout
              slot="activator"
              class="annotation-container"
              :data-annotationid="index"
              v-if="annotation.shape_type === 'check'"
              :style="
                'bottom:' +
                  annotation.position_data.bottom +
                  'px; left:' +
                  annotation.position_data.left +
                  'px;'
              "
            >
              <v-icon :style="annotation.style">done</v-icon>
              <span v-if="annotation.text">{{ annotation.text }}</span>
            </v-layout>
            <v-layout
              column
              slot="activator"
              class="annotation-container"
              :data-annotationid="index"
              v-else-if="annotation.shape_type === 'textbox'"
              :style="
                'bottom:' +
                  annotation.position_data.bottom +
                  'px; left:' +
                  annotation.position_data.left +
                  'px;'
              "
            >
              <v-flex :style="annotation.style" class="text-field-transparent">
                {{ annotation.text }}
                <v-tooltip right>
                  <v-icon
                    slot="activator"
                    style="cursor: pointer"
                    @click="openTextboxEditDialog(annotation.text, index)"
                    class="primary--text"
                    size="15"
                    >edit</v-icon
                  >
                  <span>Edit Text</span>
                </v-tooltip>
              </v-flex>
            </v-layout>
            <span
              >{{ annotation.user_name }},
              {{ moment(annotation.created_at).format('lll') }}</span
            >
          </v-tooltip>
        </v-layout>
      </v-container>
    </v-layout>

    <v-flex
      v-else-if="['docx', 'pptx', 'xlsx', 'xls', 'doc'].includes(previewType)"
    >
      <iframe
        v-if="previewUrl"
        :src="previewUrl"
        style="width: 100%; overflow-y: scroll"
        frameborder="0"
        scrolling="no"
        height="500px"
      />
    </v-flex>
    <v-layout no-wrap v-else-if="previewType" style="overflow: scroll">
      <v-layout v-if="errorMessage" class="py-3" align-start>
        <v-icon class="material-icons-outlined mr-2">info</v-icon>
        <span style="font-size: 16px">
          {{ errorMessage }}
        </span>
      </v-layout>

      <v-flex
        :class="{ 'image-container': $vuetify.breakpoint.mdAndUp }"
        :style="`transform: rotate(${previewRotation}deg);max-height:auto;`"
      >
        <!-- <v-img v-if="heicLoaded" :src="convertedDocumentLink" alt="Preview Not Available."/> -->
        <v-img
          :src="documentPreviewData"
          alt="Preview Not Available."
          class="ph-no-capture"
        />
      </v-flex>
    </v-layout>
    <v-flex
      class="text-xs-center grey--text text--darken-2 ma-5 subheading"
      v-else
    >
      Preview is not available for this file. Please download it instead.
    </v-flex>
  </v-container>
</template>
<script>
import moment from 'moment';
import pdf from 'vue-pdf';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import axios from 'axios';
import interact from 'interactjs';

export default {
  name: 'previewDocument',
  components: {
    pdf,
  },
  props: {
    documentPreviewData: {
      required: true,
      type: String,
    },
    previewType: {
      required: false,
      type: String,
    },
    previewRotation: {
      required: false,
      type: Number,
    },
    showAnnotations: {
      type: Boolean,
      default: false,
    },
    selectedItem: {
      type: Object,
      required: false,
    },
    annotations: {
      type: Array,
      default() {
        return [];
      },
    },
    hideRotateButton: {
      type: Boolean,
    },
    style: {
      type: String,
    },
  },
  data() {
    return {
      zoom: true,
      pdfLoaded: false,
      currentPage: 1,
      pageCount: 1,
      heicLoaded: false,
      processedAnnotations: [],
      shapeType: 'check',
      selectedColor: 'black',
      moving: false,
      arrowDrawing: false,
      textboxDialogOpened: false,
      textboxText: '',
      selectedIndex: null,
      selectedWidth: 0,
      selectedHeight: 0,
      errorMessage: null,

      passwordDialogOpened: false,
      pdfPassword: '',
      passwordReason: '',
      updatePassword: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
    }),
    previewUrl() {
      var encodedUrl = encodeURIComponent(this.documentPreviewData);
      return (
        'https://docs.google.com/viewer?url=' + encodedUrl + '&embedded=true'
      );
    },
    annotationsInSamePage() {
      return this.processedAnnotations.filter(a => a.page === this.currentPage);
    },
  },
  methods: {
    moment,
    get,
    isEmpty,

    handlePassword(updatePassword, reason) {
      this.updatePassword = updatePassword;
      this.passwordReason = reason;
      this.pdfPassword = '';
      this.passwordDialogOpened = true;
      console.log(updatePassword);
    },

    submitPassword() {
      if (this.updatePassword) {
        this.updatePassword(this.pdfPassword);
      }

      this.passwordDialogOpened = false;
    },

    openTextboxEditDialog(text, index) {
      this.textboxText = text;
      this.selectedIndex = index;
      this.textboxDialogOpened = true;
    },
    saveTextbox() {
      this.annotations[this.selectedIndex].text = this.textboxText;
      this.textboxDialogOpened = false;
      this.$emit('update:annotationsChanged', true);
      this.renderAnnotations();
    },
    takeAction(index) {
      if (this.shapeType === 'delete') {
        this.annotations.splice(index, 1);
        this.$emit('update:annotationsChanged', true);
        this.renderAnnotations();
      }
    },
    findRawAnnotation(index) {
      return this.annotations[index];
    },
    getLocation(event) {
      if (this.showAnnotations) {
        let pdfContainer = document.querySelector('.pdf-container');
        let box = pdfContainer.getBoundingClientRect();
        if (this.shapeType === 'check') {
          this.annotations.push({
            shape_type: 'check',
            style: `color: ${this.selectedColor}`,
            text: null,
            user_name: this.currentUser.full_name,
            page: this.currentPage,
            position_data: {
              width: 25,
              height: 20,
              bottom: box.top + box.height - event.clientY.toString() - 10,
              left: event.clientX.toString() - box.left - 10,
              pageWidth: box.width,
              pageHeight: box.height,
            },
          });
          this.$emit('update:annotationsChanged', true);
        } else if (this.shapeType === 'textbox') {
          this.annotations.push({
            shape_type: 'textbox',
            style: `width: 200; height: 100; color: ${this.selectedColor}`,
            text: 'Text Box',
            page: this.currentPage,
            user_name: this.currentUser.full_name,
            position_data: {
              width: 200,
              height: 100,
              bottom: box.top + box.height - event.clientY.toString() - 10,
              left: event.clientX.toString() - box.left - 10,
              pageWidth: box.width,
              pageHeight: box.height,
            },
          });
          this.shapeType = 'move';
          this.openTextboxEditDialog('Text Box', this.annotations.length - 1);
          this.$emit('update:annotationsChanged', true);
        }
        this.renderAnnotations();
      }
    },
    renderAnnotations() {
      let pdfContainer = document.querySelector('.pdf-container');
      let box = pdfContainer.getBoundingClientRect();
      this.processedAnnotations = this.annotations.map(annotation => {
        const ratio = box.width / annotation.position_data.pageWidth;
        return {
          position_data: {
            pageWidth: box.width,
            pageHeight: box.height,
            width: annotation.position_data.width * ratio,
            height: annotation.position_data.height * ratio,
            bottom: annotation.position_data.bottom * ratio,
            left: annotation.position_data.left * ratio,
          },
          ...annotation,
        };
      });
    },
    checkUrl(url) {
      this.errorMessage = null;

      axios.get(url).catch(error => {
        if (error.response) {
          this.errorMessage =
            "Sorry we couldn't load this file. It might have been renamed, moved or deleted.";
        }
      });
    },
  },
  created() {
    interact('.annotation-container').draggable(false);
    this.checkUrl(this.documentPreviewData);
  },
  watch: {
    documentPreviewData: {
      handler(value) {
        if (value) {
          this.errorMessage = null;
          this.checkUrl(value);
        }
      },
    },
    shapeType: {
      handler(value) {
        const pdfContainer = document.querySelector('.pdf-container');
        const {
          width: pageWidth,
          height: pageHeight,
        } = pdfContainer.getBoundingClientRect();
        if (value === 'move') {
          interact('.annotation-container').draggable({
            listeners: {
              start: event => {
                this.moving = true;
              },
              move: event => {
                const index = parseInt(event.target.dataset['annotationid']);
                const annotation = this.annotations[index];
                const ratio = pageWidth / annotation.position_data.pageWidth;
                const newLeft = annotation.position_data.left + event.dx;
                const newBottom = annotation.position_data.bottom - event.dy;
                if (newLeft > 0) {
                  annotation.position_data.left = newLeft;
                  event.target.style.left = newLeft + 'px';
                }
                if (newBottom > 0) {
                  annotation.position_data.bottom = newBottom;
                  event.target.style.bottom = newBottom + 'px';
                }
              },
              end: event => {
                const index = parseInt(event.target.dataset['annotationid']);
                const annotation = this.annotations[index];

                this.annotations.splice(index, 1, annotation);
                this.moving = false;
                this.$emit('update:annotationsChanged', true);
              },
            },
          });
        } else {
          interact('.annotation-container').draggable(false);
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.sticky-toolbar {
  position: sticky;
  top: 0;
  z-index: 1;
}
.image-holder {
  display: block;
  width: 200px;
  height: 200px;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}
.pdf-container {
  margin: 20px;
  padding: 0;
  position: relative;
}

.annotation-container {
  position: absolute;
}
.image-container {
  margin: 80px;
}
.annotation-date {
  font-family: 'Cedarville Cursive', cursive;
}

.sign-here-button {
  cursor: move;
  opacity: 0.9;
  width: 25%;
  height: 65%;
  width: 100%;
}
.text-field-transparent .v-input__slot {
  background: transparent !important;
}
.text-field-transparent {
  border: solid 0.2px #b2b2b2 !important;
}
</style>
